import { EmptyState } from '@/components/shared/EmptyState'
import { ManualStoryDialog } from '@/components/storybank/ManualStoryDialog'
import { StoryBankToolbar, type StorySort } from '@/components/storybank/StoryBankToolbar'
import { StoryCapBanner } from '@/components/storybank/StoryCapBanner'
import { StoryCard } from '@/components/storybank/StoryCard'
import { StoryDetailSheet } from '@/components/storybank/StoryDetailSheet'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useStoryBank } from '@/hooks/useStoryBank'
import { COMPETENCY_LABELS } from '@/lib/competency'
import type { Competency, CreateStoryInput, StoryBankEntry, StoryDetail } from '@/types/interview'
import { BookOpen, Star } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

// Derived from the canonical labels — no hardcoded duplicate list
const COMPETENCY_TABS: { key: 'all' | Competency; label: string }[] = [
  { key: 'all', label: 'All' },
  ...(Object.entries(COMPETENCY_LABELS) as [Competency, string][]).map(([key, label]) => ({
    key,
    label,
  })),
]

// Shared by the `filtered` memo and the search-analytics effect so the count
// reported to PostHog always matches what the user actually sees.
function matchesSearch(story: StoryBankEntry, query: string): boolean {
  const haystack = [
    story.title,
    story.questionText ?? '',
    story.storyText ?? '',
    story.notes ?? '',
    story.star?.situation ?? '',
    story.star?.task ?? '',
    story.star?.action ?? '',
    story.star?.result ?? '',
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(query)
}

export default function StoryBankPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    stories,
    storyCount,
    isPro,
    cap,
    hasLoaded,
    error,
    fetchStories,
    createStory,
    getStory,
    updateStory,
    deleteStory,
  } = useStoryBank()
  const analytics = useAnalytics()

  const [activeTab, setActiveTab] = useState<'all' | Competency>('all')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<StorySort>('newest')
  const [addOpen, setAddOpen] = useState(false)
  const [capHit, setCapHit] = useState(false)
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())
  const viewedOnce = useRef(false)

  const openStoryId = searchParams.get('story')

  useEffect(() => {
    void fetchStories()
  }, [fetchStories])

  useEffect(() => {
    // hasLoaded (not !loading) — loading starts false, so gating on !loading
    // alone can fire this event before the first fetch has actually completed.
    if (hasLoaded && !viewedOnce.current && !error) {
      viewedOnce.current = true
      analytics.trackStoryBankViewed({ story_count: stories.length, is_pro: isPro })
    }
  }, [hasLoaded, error, stories.length, isPro, analytics])

  const filtered = useMemo(() => {
    let result = activeTab === 'all' ? stories : stories.filter((s) => s.competency === activeTab)

    const q = search.trim().toLowerCase()
    if (q) {
      result = result.filter((s) => matchesSearch(s, q))
    }

    const sorted = [...result]
    if (sort === 'score') {
      sorted.sort((a, b) => (b.scores?.overall ?? 0) - (a.scores?.overall ?? 0))
    } else if (sort === 'competency') {
      sorted.sort((a, b) => (a.competency ?? 'zz').localeCompare(b.competency ?? 'zz'))
    }
    // 'newest' keeps server order (created_at DESC)
    return sorted
  }, [stories, activeTab, search, sort])

  // Debounced search analytics — fires the same count the user sees (derived
  // from `filtered`, which already applies the tab filter + all searchable
  // fields), once per settled query. The effect's own cleanup both re-arms the
  // debounce on every keystroke and clears the timer on unmount.
  useEffect(() => {
    if (!search.trim()) return
    const timer = setTimeout(() => {
      analytics.trackStoryBankSearched({ result_count: filtered.length })
    }, 800)
    return () => clearTimeout(timer)
  }, [search, filtered, analytics])

  function handleSearchChange(value: string) {
    setSearch(value)
  }

  const favorites = useMemo(() => stories.filter((s) => s.isFavorite), [stories])

  async function handleDelete(storyId: string) {
    const story = stories.find((s) => s.id === storyId)
    setDeletingIds((prev) => new Set(prev).add(storyId))
    const ok = await deleteStory(storyId)
    if (ok && story) {
      analytics.trackStoryDeleted({ competency: story.competency ?? 'unknown' })
    }
    setDeletingIds((prev) => {
      const next = new Set(prev)
      next.delete(storyId)
      return next
    })
  }

  async function handleCreate(input: CreateStoryInput) {
    const result = await createStory(input)
    if (result.ok) {
      analytics.trackStorySaved({
        competency: input.competency,
        difficulty: 'none',
        source: input.source,
      })
    }
    return result
  }

  function handleCapHit(count: number) {
    setCapHit(true)
    analytics.trackStoryCapHit({ story_count: count })
  }

  const openStory = useCallback(
    (storyId: string) => {
      setSearchParams({ story: storyId }, { replace: false })
    },
    [setSearchParams]
  )

  const closeStory = useCallback(() => {
    setSearchParams({}, { replace: false })
  }, [setSearchParams])

  const handleStoryViewed = useCallback(
    (story: StoryDetail) => {
      analytics.trackStoryViewed({
        competency: story.competency ?? 'unknown',
        source: story.source,
      })
    },
    [analytics]
  )

  const handlePatch = useCallback(
    async (storyId: string, patch: Parameters<typeof updateStory>[1]) => {
      const ok = await updateStory(storyId, patch)
      if (ok) {
        if (patch.isFavorite !== undefined && Object.keys(patch).length === 1) {
          analytics.trackStoryFavorited({ favorited: patch.isFavorite })
        } else {
          analytics.trackStoryUpdated({ fields: Object.keys(patch) })
        }
      }
      return ok
    },
    [updateStory, analytics]
  )

  // Gated on hasLoaded (not loading) so a background refetch — e.g. the one
  // createStory triggers after a successful create — doesn't blank/flash
  // already-rendered content while it's in flight.
  const showCapBanner = !isPro && cap !== null && hasLoaded && !error

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Dashboard
        </button>
      </div>
      <div className="max-w-2xl mx-auto space-y-5 mt-4">
        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Story Bank</h1>
          <p className="text-sm text-muted-foreground">
            Your STAR story library — review it before any real interview.
          </p>
          <Link
            to="/story-intake?from=story_bank"
            className="inline-flex items-center gap-1 pt-1 text-xs font-medium text-primary hover:underline"
          >
            ✨ Not sure what to write? Build stories with a short chat →
          </Link>
        </div>

        {/* Free-tier cap meter */}
        {showCapBanner && <StoryCapBanner storyCount={storyCount} cap={cap} />}

        {/* Cap-hit conversion moment */}
        {capHit && (
          <div className="rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 flex items-center justify-between gap-3">
            <p className="text-xs">
              <span className="font-semibold">Your 3 free story slots are full.</span>{' '}
              <span className="text-muted-foreground">
                Upgrade to keep every story you ever practice.
              </span>
            </p>
            <Link
              to="/settings"
              className="flex-shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Upgrade to Pro
            </Link>
          </div>
        )}

        {/* Toolbar */}
        <StoryBankToolbar
          search={search}
          onSearchChange={handleSearchChange}
          sort={sort}
          onSortChange={setSort}
          isPro={isPro}
          onAddStory={() => setAddOpen(true)}
        />

        {/* Competency filter tabs */}
        <div className="flex gap-2 flex-wrap">
          {COMPETENCY_TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={
                activeTab === key
                  ? 'rounded-full px-3 py-1 text-xs font-medium bg-primary text-primary-foreground'
                  : 'rounded-full px-3 py-1 text-xs font-medium bg-muted text-muted-foreground hover:text-foreground transition-colors'
              }
            >
              {label}
            </button>
          ))}
        </div>

        {/* Best answers strip */}
        {hasLoaded && !error && favorites.length > 0 && activeTab === 'all' && !search && (
          <div className="space-y-2">
            <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              Best answers
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {favorites.map((story) => (
                <StoryCard
                  key={`fav-${story.id}`}
                  story={story}
                  onOpen={openStory}
                  onDelete={handleDelete}
                  isDeleting={deletingIds.has(story.id)}
                />
              ))}
            </div>
            <div className="border-t border-border" />
          </div>
        )}

        {/* Content */}
        {/* Skeleton only for the initial load — hasLoaded stays true across
            background refetches (e.g. after create), so the real list below
            keeps rendering instead of flashing back to this grid. */}
        {!hasLoaded && (
          <div className="grid gap-3 sm:grid-cols-2">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="rounded-xl border border-border bg-background p-4 space-y-3 animate-pulse"
              >
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
                <div className="h-3 bg-muted rounded w-full" />
                <div className="h-3 bg-muted rounded w-4/5" />
              </div>
            ))}
          </div>
        )}

        {hasLoaded && error && (
          <div className="rounded-xl border border-border bg-background px-4 py-4 flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              type="button"
              onClick={() => void fetchStories()}
              className="flex-shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {hasLoaded && !error && filtered.length === 0 && (
          <EmptyState
            icon={<BookOpen className="h-5 w-5" />}
            title={
              search
                ? 'No stories match your search'
                : activeTab === 'all'
                  ? 'Your story bank is empty'
                  : `No ${COMPETENCY_LABELS[activeTab as Competency] ?? activeTab} stories`
            }
            description={
              search
                ? 'Try a different search term or clear the search.'
                : activeTab === 'all'
                  ? 'Add a story from your classes, clubs, or jobs — or save an answer after a practice session.'
                  : 'Try a different filter, or add a story in this competency.'
            }
            action={
              !search && activeTab === 'all' ? (
                <div className="flex flex-wrap justify-center gap-2">
                  <Link
                    to="/story-intake?from=story_bank"
                    className="rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                  >
                    Build my bank in ~10 minutes
                  </Link>
                  <button
                    type="button"
                    onClick={() => setAddOpen(true)}
                    className="rounded-lg border border-border px-4 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
                  >
                    Write one myself
                  </button>
                </div>
              ) : undefined
            }
          />
        )}

        {hasLoaded && !error && filtered.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {filtered.map((story) => (
              <StoryCard
                key={story.id}
                story={story}
                onOpen={openStory}
                onDelete={handleDelete}
                isDeleting={deletingIds.has(story.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail view — deep-linkable via ?story=<id> */}
      <StoryDetailSheet
        storyId={openStoryId}
        isPro={isPro}
        loadStory={getStory}
        onPatch={handlePatch}
        onDelete={handleDelete}
        onClose={closeStory}
        onViewed={handleStoryViewed}
      />

      {/* Manual create */}
      <ManualStoryDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={handleCreate}
        onCapHit={handleCapHit}
      />
    </>
  )
}
